import { useState, useEffect, useContext, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { useSelector, useDispatch } from "react-redux";
import { isEmpty, debounce, cloneDeep, isEqual } from "lodash";
import { eachDeep } from "deepdash-es/standalone";
import { treeActions } from "../../../redux/slices/treeSlice";
import { FormControl, InputLabel, MenuItem, Select, TextField, Stack, Typography } from "@mui/material";
import Accordion from '@mui/material/Accordion';
import AccordionSummary from '@mui/material/AccordionSummary';
import AccordionDetails from '@mui/material/AccordionDetails';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { t } from "i18next";
import { ParentScreenCtx } from "/src/pages/workbench_build/WorkbenchBuild";
import SourceType from "../../TemplateProperties/SourceType";
import { SectionHeader } from "./SectionHeader";
import { ProcessingOptionsSection } from "./OutboundFieldFormattingOptions/ProcessingOptionsSection";
import { filterOutNodeTypes } from "../../../utils/utilFunctions";
import AdvancedProperties from "./Advanced Properties/AdvancedProperties";
import { LoadingEase } from "../../LoadingEase";
import { validateAndFixElementName } from "../../../utils/validate";
import { ElementFormattingOptionsSection } from "./OutboundFieldFormattingOptions/ElementFormattingOptionsSection"
import { LOOP_RECORD } from "../../../constants/loopRecordOptions";
import VariableCollection from "../OutboundRecordProperties/VariableCollection";
import CustomDBField from "../OutboundRecordProperties/CustomDBField";
import SourceTypeInboundDataRenderer from "../../TemplateProperties/SourceTypeComponents/SourceTypeInboundDataRenderer";
import { api } from "../../../redux/api";
import { RecordPropertiesCheckBox, filterNodes } from "../OutboundRecordProperties/OutboundRecordProperties";
import { inboundTemplateToRcTree } from "../../../utils/rctreeDataConversion";

export default function OutboundElementPropertyForm() {
  //util hooks
  const { t } = useTranslation();
  const dispatch = useDispatch();

  //redux 
  const parentScreen = useContext(ParentScreenCtx);
  const queryParams = useSelector((state) => state.queryParams);
  const { rightTree } = useSelector((state) => state.treeData[parentScreen]);
  const translatedData = rightTree.tree[rightTree.currentTab].translatedData;
  const selectedNode = rightTree.tree[rightTree.currentTab].selectedNode;
  const templateHeader = rightTree.tree.templateHeader;
  const account = queryParams.account;
  const DEPENDENCIES = useSelector(state => state.treeData.outbound.rightTree.tree.templateHeader?.DEPENDENCIES || '');
  const inboundTemplateTree = useSelector(state => state.treeData.inbound.leftTree.tree.body.translatedData);

  const fieldTypeDefault = selectedNode?.FIELD?.FIELDTYPE ? selectedNode?.FIELD?.FIELDTYPE : selectedNode?.FIELDTYPE ? selectedNode?.FIELDTYPE : "ALPHA"
  //state declarations
  const [fieldType, setFieldType] = useState(fieldTypeDefault);
  const [sourceNodeInfo, setSourceNodeInfo] = useState({ FIELDTYPE: "ALPHA" });
  const [fieldPropTopLoaded, setFieldPropTopLoaded] = useState(true);
  const [updatedSelectedNode, setUpdatedSelectedNode] = useState({ ...selectedNode });
  const [updatedTPHeader, setUpdatedTPHeader] = useState({ ...templateHeader });
  const [ftClone, setFtClone] = useState(false);
  const elementNameDefault = selectedNode?.ELEMENT_NAME
    || (selectedNode?.xmlroot && templateHeader?.XML_BODY_NAME ? templateHeader.XML_BODY_NAME : '')
    || '';
  const [elementName, setElementName] = useState(elementNameDefault);
  const [elementNamePrev, setElementNamePrev] = useState(false);
  const [elementNameErrors, setElementNameErrors] = useState([]);

  useEffect(() => {
    const isRootXmlNode = selectedNode?.xmlroot && (selectedNode.key === "ob-0" || selectedNode.key === "obh-0" || selectedNode.key === "obt-0");
    if (!isRootXmlNode) return;

    const preferredName = selectedNode?.ELEMENT_NAME || templateHeader?.XML_BODY_NAME || '';
    if (elementName !== preferredName) setElementName(preferredName);
  }, [templateHeader?.XML_BODY_NAME, selectedNode?.xmlroot, selectedNode?.key, selectedNode?.ELEMENT_NAME]);

  useEffect(() => {
    // setFieldType(selectedNode?.FIELDTYPE || "ALPHA");
    setFieldType(selectedNode?.FIELD?.FIELDTYPE || selectedNode?.FIELDTYPE || "ALPHA");
  },[selectedNode?.FIELDTYPE])

  useEffect(() => {
    if (selectedNode?.FIELD) {
      setSourceNodeInfo({
        PARENT: selectedNode.FIELD.PARENT,
        MAPNAME: selectedNode.FIELD.MAPNAME,
        RECORD_TYPE: selectedNode.FIELD.RECORD_TYPE,
        SOURCE_TYPE: selectedNode.FIELD.SOURCE_TYPE,
        MAP_REPETITION_INDEX: selectedNode.FIELD.MAP_REPETITION_INDEX,
        SOURCE_REPEATING_FIELD: selectedNode.FIELD.SOURCE_REPEATING_FIELD,
        INDEX: selectedNode.FIELD.INDEX,
        ACTION: selectedNode.FIELD.ACTION,
        CUSTOM_DB_FIELD: selectedNode.FIELD.CUSTOM_DB_FIELD,
        PLUGIN_NAME: selectedNode.FIELD.PLUGIN_NAME,
        WF_ITEM_ID: selectedNode.FIELD.WF_ITEM_ID,
        WF_ITEM_LOCATION: selectedNode.FIELD.WF_ITEM_LOCATION,
        FIELDTYPE: selectedNode.FIELD.FIELDTYPE
      });
    } else {
      // Clear sourceNodeInfo if no FIELD data
      setSourceNodeInfo({ FIELDTYPE: "ALPHA" });
    }
    setUpdatedSelectedNode({ ...selectedNode })
    if (!selectedNode?.xmlroot && selectedNode?.ELEMENT_NAME) {
      setElementNamePrev(selectedNode?.ELEMENT_NAME)
      setElementName(selectedNode?.ELEMENT_NAME)
    }
  }, [selectedNode?.key])

  const loopRecordDefault = selectedNode ?
    (selectedNode.SOURCE_TYPE && selectedNode.SOURCE_TYPE === "INBOUND_DATA") ?
      "INBOUND_DATA"
      : (selectedNode.TYPE && LOOP_RECORD.map(obj => obj.value).includes(selectedNode.TYPE)) ?
        selectedNode.TYPE
        : t('SELECT')
    : t('SELECT');


  // let lrvDefault = t('SELECT');

  // //loop record initial value edge cases.
  // if (selectedNode) {
  //   if (selectedNode.SOURCE_TYPE && selectedNode.SOURCE_TYPE === "INBOUND_DATA") {
  //     lrvDefault = "INBOUND_DATA";
  //   } else {
  //     if (selectedNode.TYPE && LOOP_RECORD.map(obj => obj.value).includes(selectedNode.TYPE)) {
  //       lrvDefault = selectedNode.TYPE
  //     }
  //   }
  // }

  //Copied states from OutboundRecordProperties.jsx to make loop source work for now.
  //Element properties uses TYPE to identify the loop record dropdown value, and LOOP_SOURCE_NAME can be the value or sub-value depending on the case.

  const [loopRecordValue, setLoopRecordValue] = useState(loopRecordDefault);
  const [treeData, setTreeData] = useState({ header: [], body: [], trailer: [] });
  const [expandActions, setExpandActions] = useState({ tab: '', keys: [] });
  const [tab, setTab] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [showCheckboxBefore, setShowCheckboxBefore] = useState(false);
  const [showCheckbox, setShowCheckbox] = useState(true);

  const saveTpHeader = (newHeader, oldHeader) => {
    //for now, only need to write to XML_BODY_NAME.
    if (newHeader.XML_BODY_NAME !== oldHeader.XML_BODY_NAME) {
      const updatedHeader = { ...cloneDeep(templateHeader), XML_BODY_NAME: newHeader.XML_BODY_NAME };
      dispatch(
        treeActions.setRightTreeTemplateHeader({
          screen: parentScreen,
          value: updatedHeader,
        })
      );
    }
  }

  const saveNewData = (newNode, sNode, tData) => {

    let nNode = { ...newNode };
    let updatedData = eachDeep(cloneDeep(tData), (obj, key, parentArr, ctx) => {
      if (typeof obj !== 'object') return;
      if (obj?.nodeId && obj.nodeId === sNode.nodeId) {
        Object.keys(newNode).forEach((prop) => {
          if (prop === "FIELD") {
            Object.keys(newNode[prop]).forEach((p) => {
              if (newNode[prop][p] === "" || newNode[prop][p] === undefined || newNode[prop][p] === null) {
                if (obj?.[prop]?.[p]) delete obj[prop][p];
                if (nNode?.[prop]?.[p]) delete nNode[prop][p];
              } else {
                if (p.toUpperCase() === p || p === "title") {
                  if (!obj[prop]) obj[prop] = {};
                  obj[prop][p] = newNode[prop][p];
                }
              }
            })
            if(obj?.[prop] && Object.keys(obj[prop]).length === 0) delete obj[prop];
            if(nNode?.[prop] && Object.keys(nNode[prop]).length === 0) delete nNode[prop];
          } else {
            if (newNode[prop] === "" || newNode[prop] === undefined || newNode[prop] === null) {
              delete obj[prop];
              delete nNode[prop];
            } else {
              if (prop.toUpperCase() === prop || prop === "title") obj[prop] = newNode[prop];
            }
          }
        });
      }
    });

    //prevent unnecessary dispatch
    if (isEqual(tData, updatedData) || isEqual(newNode, sNode)) return;

    //set new trans data to updatedData.
    dispatch(
      treeActions.setNewRightTreeTranslatedData({
        screen: parentScreen,
        type: rightTree.currentTab,
        data: updatedData,
      })
    );

    //set selectedNode to match.
    dispatch(
      treeActions.setRightTreeSelectedNode({
        screen: parentScreen,
        type: rightTree.currentTab,
        data: { ...nNode, avoidSelection: true },
      })
    );
  }

  const debouncedNodeSave = useCallback(
    debounce((node, sNode, tData) => saveNewData(node, sNode, tData), 50, true), []
  )

  const debouncedTPHeaderSave = useCallback(
    debounce((newHeader, oldHeader) => saveTpHeader(newHeader, oldHeader), 50, true), []
  )

  const modifyUpdatedNode = (prop, val, fieldLevel = false) => {
    // colorLog('#33ffcc', `modifyUpdatedNode: ${prop} ${val}`);

    if (typeof prop === "string") {
      if ((fieldLevel || prop === "FIELDTYPE")) {
        if (val !== "" && val !== undefined) {
          setUpdatedSelectedNode(prev => {
            return { ...prev, FIELD: { ...prev.FIELD, [prop]: val } }
          });
        } else {
          // setUpdatedSelectedNode(prev => {
          //   return { ...prev, FIELD: { ...prev.FIELD, [prop]: null } }
          // });
          setUpdatedSelectedNode(prev => {
            const newField = { ...(prev.FIELD || {}) };
            delete newField[prop];
            return { ...prev, FIELD: newField }
          });
        }
      } else if (prop === "ADVANCE") {
        // Handle ADVANCE property for XML elements - store inside FIELD object
        setUpdatedSelectedNode(prev => {
          const newNode = { ...prev };
          
          // Create a new FIELD object with existing properties plus advanced properties
          const newField = { ...(prev.FIELD || {}) };
          
          // Only add properties that have actual values (not undefined, null, or empty)
          if (val?.SUBSTRING !== undefined && val?.SUBSTRING !== null && val?.SUBSTRING !== '') {
            newField.SUBSTRING = val.SUBSTRING;
          }
          if (val?.FUNCTION !== undefined && val?.FUNCTION !== null && val?.FUNCTION !== '') {
            newField.FUNCTION = val.FUNCTION;
          }
          if (val?.MASK !== undefined && val?.MASK !== null && val?.MASK !== '') {
            newField.MASK = val.MASK;
          }
          if (val?.REPLACE !== undefined && val?.REPLACE !== null && val?.REPLACE !== '') {
            newField.REPLACE = val.REPLACE;
          }
          
          newNode.FIELD = newField;
          
          return newNode;
        });
      } else {
        if (val !== "" && val !== undefined) {
          setUpdatedSelectedNode(prev => {
            return { ...prev, [prop]: val }
          });
        } else {
          setUpdatedSelectedNode(prev => {
            return { ...prev, [prop]: null }
          });
        }
      }
    } else {
      //obj of props to change
      setUpdatedSelectedNode(prev => {
        return { ...prev, ...prop }
      })
    }
  }

  const modifyTPHeader = (prop, val) => {
    //template header : XML_BODY_NAME (Element Name)
    const newHeader = { ...cloneDeep(templateHeader), [prop]: val };
    const oldHeader = { ...cloneDeep(templateHeader) };
    saveTpHeader(newHeader, oldHeader);
  }

  const handleFieldTypeChange = (e) => {
    setFieldType(e?.target?.value || "ALPHA");
    modifyUpdatedNode("FIELDTYPE", e?.target?.value || "ALPHA");

    if (e?.target?.value === "CONSTANT") {
      // modifyUpdatedNode("title", `(${t("CONSTANT")}) ${updatedSelectedNode.DESCRIPTION}`);

      //constant removes source type data.
      setSourceNodeInfo({});
      modifyUpdatedNode("MAPNAME", "");
      modifyUpdatedNode("RECORD_TYPE", "");
      modifyUpdatedNode("SOURCE_TYPE", "");

    } else if (e?.target?.value === "FLOAT") {
      // Automatically enable Allow Rounding for float values
      modifyUpdatedNode("ROUND", "TRUE");
    } else if (e?.target?.value) {
      // modifyUpdatedNode("title", `${updatedSelectedNode.DESCRIPTION}`);
    };

    //on filetype change, remove any properties that are not allowed for the new type.
    if (updatedSelectedNode.FIELDTYPE !== e?.target?.value) {
      let dtProps = fieldpropertiesControls[templateHeader.FILETYPE];
      fieldpropertiesControls.ALL.forEach(prop => {
        if (!dtProps[e?.target?.value || "ALPHA"].includes(prop)) {
          if (updatedSelectedNode[prop]) modifyUpdatedNode(prop, null);
        }
      });
    }
    setFtClone(e?.target?.value || "ALPHA");
  }

  const handleDescriptionSave = (newVal) => {
    if (updatedSelectedNode.title !== `<${newVal}>`) modifyUpdatedNode("title", `<${newVal}>`);
    if (selectedNode.xmlroot) {
      //if first body node, write to template header instead of node iteself.
      if (updatedTPHeader.XML_BODY_NAME !== newVal)  modifyTPHeader("XML_BODY_NAME", newVal);
    } else {
      if (updatedSelectedNode.ELEMENT_NAME !== newVal) modifyUpdatedNode("ELEMENT_NAME", newVal);
    }
  }

  const handleDescriptionChange = (newVal) => {
    setElementName(newVal);
    
    // Clear errors on typing for real-time feedback
    if (elementNameErrors.length > 0) {
      setElementNameErrors([]);
    }
    
    // For XML templates, provide real-time validation feedback
    if (templateHeader?.FILETYPE === "XML" && newVal) {
      const validation = validateAndFixElementName(newVal);
      if (!validation.isValid) {
        // Set errors for immediate feedback but don't auto-correct until blur
        setElementNameErrors(validation.errors);
      }
    }
  }
  const validateElementName = (newVal) => {
    // Use the comprehensive validation from Java code
    const validation = validateAndFixElementName(newVal);
    
    // Set error messages for display
    setElementNameErrors(validation.errors);
    
    // For XML templates, use stricter validation
    if (templateHeader?.FILETYPE === "XML") {
      return validation.isValid;
    }
    
    if (newVal === "" || newVal === null) return false;
    if (newVal.includes(":")) return false;
    if (newVal.includes("*")) return false;
    if (newVal.includes("?")) return false;
    return true;
  }
  //blur handler acts like a cleanup filter
  const handleDescriptionBlur = (newVal, e) => {
    // Clear previous errors
    setElementNameErrors([]);
    
    // For XML templates, apply comprehensive validation and auto-correction
    if (templateHeader?.FILETYPE === "XML") {
      const validation = validateAndFixElementName(newVal);
      
      if (!validation.isValid && validation.correctedName) {
        // Auto-correct the element name
        setElementName(validation.correctedName);
        handleDescriptionSave(validation.correctedName);
        
        // Show validation errors as warnings (auto-corrected)
        if (validation.errors.length > 0) {
          console.warn('Element name auto-corrected:', validation.errors);
        }
        return;
      } else if (!validation.isValid && !validation.correctedName) {
        // Cannot auto-correct, revert to previous value
        setElementName(elementNamePrev);
        setElementNameErrors(validation.errors);
        return;
      }
    } else {
      // For non-XML templates, use basic validation
      if (!validateElementName(newVal)) {
        setElementName(elementNamePrev);
        return;
      }
    }
    
    // Handle empty values
    if ((newVal === "" || newVal === null) && elementNamePrev !== false && elementNamePrev) {
      if (elementNamePrev !== elementName) {
        setElementName(elementNamePrev);
        handleDescriptionSave(elementNamePrev);
      }
    } else {
      handleDescriptionSave(newVal);
    }
  }

  const handleDescriptionFocus = (e) => {
    setElementNamePrev(e.target.value);
  }

  // ** LOOP_RECORD functions **

  const handleLoopRecordChange = (e) => {
    const val = e.target.value;
    setLoopRecordValue(val);

    if (val === "INBOUND_DATA") {
      modifyUpdatedNode({
        LOOP: "MULTI",
        ICON_DISPLAY: "REPEATING_ELEMENT"
      });
    } else {
      if (val === t("SELECT")) {
        modifyUpdatedNode({
          LOOP: "SINGLE",
          LOOP_SOURCE_NAME: null,
          PARENT: null,
          SOURCE_TYPE: null,
          TYPE: null,
          ICON_DISPLAY: "ELEMENT"
        });
      } else {
        modifyUpdatedNode({
          LOOP: "MULTI",
          LOOP_SOURCE_NAME: val || null,
          PARENT: null,
          SOURCE_TYPE: val || null,
          TYPE: val || null,
          ICON_DISPLAY: "REPEATING_ELEMENT"
        });
      }
    }


    setShowCheckboxBefore(val === "INBOUND_DATA");
  }

  const recordLoopControlValuesHandler = (values) => {
    const { PARENT, MAPNAME, RECORD_TYPE, ACTION, INDEX, MAP_REPETITION_INDEX, CUSTOM_DB_FIELD, PLUGIN_NAME, WF_ITEM_ID, SOURCE_REPEATING_FIELD, WF_ITEM_LOCATION } = values
    const updatedTranslatedData = eachDeep(cloneDeep(translatedData), (obj) => {
      if (obj && obj.key === selectedNode.key) {
        // Update properties based on values
        obj.LOOP_SOURCE_NAME = MAPNAME;
        obj.SOURCE_PARENT = PARENT;
        obj.SOURCE_TYPE = RECORD_TYPE;
        obj.ACTION = ACTION;
        obj.INDEX = INDEX;
        obj.MAP_REPETITION_INDEX = MAP_REPETITION_INDEX;
        obj.CUSTOM_DB_FIELD = CUSTOM_DB_FIELD;
        obj.PLUGIN_NAME = PLUGIN_NAME;
        obj.WF_ITEM_ID = WF_ITEM_ID;
        obj.WF_ITEM_LOCATION = WF_ITEM_LOCATION;
        obj.SOURCE_REPEATING_FIELD = SOURCE_REPEATING_FIELD || undefined;

        dispatch(
          treeActions.setRightTreeSelectedNode({
            screen: parentScreen,
            type: rightTree.currentTab,
            data: obj,
          })
        );
      }

      return obj;
    });

    dispatch(
      treeActions.setNewRightTreeTranslatedData({
        screen: parentScreen,
        type: rightTree.currentTab,
        data: updatedTranslatedData,
      })
    );
  }

  const handleSourceNameSelect = (mapname) => { }//?

  const valuesHandler = (vals) => {
    if (vals?.key !== "ib-0") {
      // Loop-level properties stay at element level
      modifyUpdatedNode({
        LOOP_SOURCE_NAME: vals?.RECORD_TYPE || null,
        TYPE: vals?.RECORD_TYPE || null,
        SOURCE_TYPE: "INBOUND_DATA",
        PARENT: vals?.PARENT || null  // PARENT at element level (for record hierarchy)
      });
      
      // Inbound mapping properties go in nested FIELD object
      // PARENT is saved at BOTH levels (element level above + FIELD level below)
      if (vals?.PARENT) modifyUpdatedNode("PARENT", vals.PARENT, true);
      if (vals?.RECORD_TYPE) modifyUpdatedNode("RECORD_TYPE", vals.RECORD_TYPE, true);
      if (vals?.MAPNAME) modifyUpdatedNode("MAPNAME", vals.MAPNAME, true);
      if (vals?.ACTION) modifyUpdatedNode("ACTION", vals.ACTION, true);
      if (vals?.INDEX) modifyUpdatedNode("INDEX", vals.INDEX, true);
      if (vals?.MAP_REPETITION_INDEX) modifyUpdatedNode("MAP_REPETITION_INDEX", vals.MAP_REPETITION_INDEX, true);
      if (vals?.SOURCE_REPEATING_FIELD) modifyUpdatedNode("SOURCE_REPEATING_FIELD", vals.SOURCE_REPEATING_FIELD, true);
    }

    return;
  };

  // Separate handler for Loop Record INBOUND_DATA (sets SOURCE_TYPE but not nested FIELD properties)
  const loopRecordInboundDataValuesHandler = (vals) => {
    if (vals?.key !== "ib-0") {
      // Set loop-level properties including SOURCE_TYPE (for dropdown state)
      // But DON'T set nested FIELD properties (to keep Data Source section independent)
      modifyUpdatedNode({
        LOOP_SOURCE_NAME: vals?.RECORD_TYPE || null,
        TYPE: vals?.RECORD_TYPE || null,
        SOURCE_TYPE: "INBOUND_DATA",
        PARENT: vals?.PARENT || null
      });
    }
    return;
  };
  const getWorkflows = () => {
    const workflows = []
    inboundTemplateTree?.forEach((ele) => {
      if (ele?.type === 'workflowGroup') {
        workflows.push(ele.title);
      }
    })
    return workflows;
  }
  const getTemplatesInWorkflowGroup = (workflow) => {
    const templates = [];
    inboundTemplateTree?.forEach((ele) => {
      if (ele?.title === workflow) {
        ele?.children?.forEach(t => (
          templates.push(t.title))
        );
        return;
      }
    })
    return templates;
  }
  // Helper function to get parent's RECORD_TYPE for inbound data mapping
  const getParentRecordType = useCallback(() => {
    // Root elements (ob-0, obh-0, obt-0) use their own TYPE
    if (updatedSelectedNode.xmlroot || updatedSelectedNode.key === "ob-0" || 
        updatedSelectedNode.key === "obh-0" || updatedSelectedNode.key === "obt-0") {
      return updatedSelectedNode.TYPE || updatedSelectedNode.FIELD?.RECORD_TYPE;
    }
    
    // For non-root elements, find parent's RECORD_TYPE
    const findParentType = (nodes, targetKey) => {
      for (const node of nodes) {
        if (node.children) {
          for (const child of node.children) {
            if (child.key === targetKey) {
              // Found the parent node
              return node.TYPE || node.RECORD_TYPE || node.FIELD?.RECORD_TYPE;
            }
          }
          // Recursively search in children
          const result = findParentType(node.children, targetKey);
          if (result) return result;
        }
      }
      return null;
    };
    
    return findParentType(translatedData, updatedSelectedNode.key) || 
           updatedSelectedNode.TYPE || 
           updatedSelectedNode.FIELD?.RECORD_TYPE;
  }, [updatedSelectedNode, translatedData]);

  const loopRecordMenuItems = LOOP_RECORD.map((obj, key) => (
    <MenuItem key={key} value={obj.value}>
      {t(obj.title)}
    </MenuItem>
  ))
  const COMPONENT_MAPPING_OBJ = {
    VARIABLE_COLLECTION: <VariableCollection updatedSelectedNode={updatedSelectedNode} modifyUpdatedNode={modifyUpdatedNode} hasUpdatedNode={true} />,
    CUSTOM_DB_FIELD: <CustomDBField updatedSelectedNode={updatedSelectedNode} modifyUpdatedNode={modifyUpdatedNode} hasUpdatedNode={true} />,
    RECORD_LOOP_CONTROL: (
      <SourceType
        node={{
          PARENT: updatedSelectedNode.SOURCE_PARENT,
          MAPNAME: updatedSelectedNode.LOOP_SOURCE_NAME,
          RECORD_TYPE: updatedSelectedNode.SOURCE_TYPE,
          key: updatedSelectedNode.key,
          ACTION: updatedSelectedNode?.ACTION,
          INDEX: updatedSelectedNode?.INDEX,
          MAP_REPETITION_INDEX: updatedSelectedNode?.MAP_REPETITION_INDEX,
          CUSTOM_DB_FIELD: updatedSelectedNode?.CUSTOM_DB_FIELD,
          PLUGIN_NAME: updatedSelectedNode?.PLUGIN_NAME
        }}
        valuesHandler={recordLoopControlValuesHandler}
        callerComponent='recordProperties'
      />
    ),
    INBOUND_DATA: (() => {
      const nodeProps = {
        PARENT: updatedSelectedNode.PARENT,
        MAPNAME: updatedSelectedNode.LOOP_SOURCE_NAME,
        LOOP_SOURCE_NAME: updatedSelectedNode.LOOP_SOURCE_NAME,
        RECORD_TYPE: updatedSelectedNode.LOOP_SOURCE_NAME ? getParentRecordType() : null,
        TYPE: updatedSelectedNode.TYPE,
        SOURCE_TYPE: updatedSelectedNode.LOOP_SOURCE_NAME ? "INBOUND_DATA" : null,
        key: updatedSelectedNode.key
      };
      return (
        <SourceTypeInboundDataRenderer
          allowAllTreeSelection={true}
          treeData={treeData}
          expandActions={expandActions}
          setTab={setTab}
          handleSourceNameSelect={handleSourceNameSelect}
          valuesHandler={loopRecordInboundDataValuesHandler}
          src="INBOUND_DATA"
          node={nodeProps}
          onlyRecord={true}
          isLoading={isLoading}
          showInboundTree={true}
        />
      );
    })(),
  };
  useEffect(() => {
    if (loopRecordValue === "INBOUND_DATA") {
      const dependency = DEPENDENCIES?.split("/") || [];

      const workflow = dependency[0] || "";
      const template = dependency[2] || "";
      const workflows = getWorkflows();
      const templatesInWorkflow = getTemplatesInWorkflowGroup(workflow);

      if (
        workflows.includes(workflow) &&
        templatesInWorkflow.includes(template)
      ) {
        setIsLoading(true);
        dispatch(
          api.endpoints.getInboundTemplateData.initiate(
            {
              workflowGroupName: workflow,
              templateName: template,
              account: account,
            },
            { forceRefetch: true }
          )
        ).then((res) => {
          const headerData = filterOutNodeTypes(
            filterNodes(inboundTemplateToRcTree(res.data, "HEADER"), "header"),
            []
          );
          const bodyData = filterOutNodeTypes(
            filterNodes(inboundTemplateToRcTree(res.data, "BODY"), "body"),
            []
          );
          const trailerData = filterOutNodeTypes(
            filterNodes(
              inboundTemplateToRcTree(res.data, "TRAILER"),
              "trailer"
            ),
            []
          );
          setTreeData((prev) => {
            return { header: headerData, body: bodyData, trailer: trailerData };
          });
          setIsLoading(false);
        });
      } else setIsLoading(false);
    }
    setShowCheckbox(loopRecordValue !== 'RECORD_LOOP_CONTROL')

  }, [DEPENDENCIES, loopRecordValue]);
  // ** END LOOP_RECORD functions **

  const updatedSelectedNodeWithAdvanceProperties = (val) => {
    modifyUpdatedNode("ADVANCE", val);
  }

  const sourceTypeValueChange = (values, type) => {
    setSourceNodeInfo(values);
    const { PARENT, MAPNAME, RECORD_TYPE, ACTION, INDEX, MAP_REPETITION_INDEX, CUSTOM_DB_FIELD, PLUGIN_NAME, WF_ITEM_ID, WF_ITEM_LOCATION, SOURCE_REPEATING_FIELD } = values;
    let newSelNode = false;
    
    // Only save to FIELD if this is actual inbound data mapping (type is "INBOUND_DATA" and we have proper mapping data)
    // const isActualInboundMapping = type === "INBOUND_DATA" && RECORD_TYPE && (PARENT || RECORD_TYPE !== MAPNAME);
    if (MAPNAME && MAPNAME !== t("SELECT") && type !== "NONE") {
      if (PARENT) modifyUpdatedNode("PARENT", PARENT, true);
      if (type) modifyUpdatedNode("SOURCE_TYPE", type, true);
      if (MAPNAME) modifyUpdatedNode("MAPNAME", MAPNAME, true);
      if (RECORD_TYPE) modifyUpdatedNode("RECORD_TYPE", RECORD_TYPE, true);
      if (MAP_REPETITION_INDEX) modifyUpdatedNode("MAP_REPETITION_INDEX", MAP_REPETITION_INDEX, true);
      if (SOURCE_REPEATING_FIELD) modifyUpdatedNode("SOURCE_REPEATING_FIELD", SOURCE_REPEATING_FIELD, true);
      if (INDEX) modifyUpdatedNode("INDEX", INDEX, true);
      if (ACTION) modifyUpdatedNode("ACTION", ACTION, true);
      if (CUSTOM_DB_FIELD) modifyUpdatedNode("CUSTOM_DB_FIELD", CUSTOM_DB_FIELD, true);
      if (PLUGIN_NAME) modifyUpdatedNode("PLUGIN_NAME", PLUGIN_NAME, true);
      if (WF_ITEM_ID) modifyUpdatedNode("WF_ITEM_ID", WF_ITEM_ID, true);
      if (WF_ITEM_LOCATION) modifyUpdatedNode("WF_ITEM_LOCATION", WF_ITEM_LOCATION, true);
      if(selectedNode?.FIELDTYPE) modifyUpdatedNode("FIELDTYPE", selectedNode?.FIELDTYPE, true);
      
    }
    if (type === "NONE") {
      if (updatedSelectedNode.RECORD_TYPE) modifyUpdatedNode("RECORD_TYPE", null);
      if (updatedSelectedNode.SOURCE_TYPE) modifyUpdatedNode("SOURCE_TYPE", null);
      if (updatedSelectedNode.MAPNAME) modifyUpdatedNode("MAPNAME", null);
      if (MAP_REPETITION_INDEX) modifyUpdatedNode("MAP_REPETITION_INDEX", null);
      if (SOURCE_REPEATING_FIELD) modifyUpdatedNode("SOURCE_REPEATING_FIELD", null);
      if (INDEX) modifyUpdatedNode("INDEX", null);
      if (ACTION) modifyUpdatedNode("ACTION", null);
      if (CUSTOM_DB_FIELD) modifyUpdatedNode("CUSTOM_DB_FIELD", null);
      if (PLUGIN_NAME) modifyUpdatedNode("PLUGIN_NAME", null);
      if (WF_ITEM_ID) modifyUpdatedNode("WF_ITEM_ID", null);
      if (WF_ITEM_LOCATION) modifyUpdatedNode("WF_ITEM_LOCATION", null);
    }

    // if (XMLAttribute) {
    //   if (updatedSelectedNode.ATTRIBUTE_NAME === undefined || updatedSelectedNode.ATTRIBUTE_NAME === "") handleDescriptionBlur("");
    // } else {
    if ((updatedSelectedNode.ELEMENT_NAME === undefined || updatedSelectedNode.ELEMENT_NAME === "") && (selectedNode.key !== "ob-0" || selectedNode.key !== "obh-0" || selectedNode.key !== "obt-0")) handleDescriptionBlur("");
    // }
  }

  const sourceTypeDropdownChange = (val) => {
    if (val !== "INBOUND_DATA") {
      setSourceNodeInfo({});
    }
  }

  useEffect(() => {
    if (!isEqual(selectedNode, updatedSelectedNode)) return debouncedNodeSave(updatedSelectedNode, selectedNode, translatedData);
  }, [updatedSelectedNode]);

  
  return (
    <>
      {!isEmpty(rightTree.tree[rightTree.currentTab].selectedNode) ? (
        <div style={styles.fpContainer}>

          {/* Loader shows while states are being set */}
          {!fieldPropTopLoaded && <LoadingEase forceStyles={{ padding: "16px 0 8px 16px" }}>{t('LOADING')} {t('LBL_FLD_PROP')}...</LoadingEase>}

          <div className="width-100" style={{ display: fieldPropTopLoaded ? "unset" : "none" }}>
            <div className="flex flex-wrap" style={{ alignItems: "flex-start", padding: "15px 15px 10px 15px", gap: "10px" }}>

              {/* Field Type - Dropdown */}
              <FormControl variant="outlined" sx={{ minWidth: "150px", width: "unset", height: "35px", flex: "1" }} size="small">
                <InputLabel id="ftDropdownLabel">{t("LBL_FLD_TYPE").replace(":", "")}</InputLabel>
                <Select
                  sx={{ height: "34px", width: "unset" }}
                  MenuProps={{
                    PaperProps: {
                      sx: {
                        maxHeight: 300,
                      },
                    },
                  }}
                  variant="outlined"
                  size="small"
                  name={"FIELDTYPE"}
                  value={fieldType}
                  labelId="ftDropdownLabel"
                  label={t("LBL_FLD_TYPE").replace(":", "")}
                  id="ftDropdown"
                  onChange={handleFieldTypeChange}
                  // style={{height: "35px"}}
                  disabled={!fieldPropTopLoaded}
                >
                  {fieldTypeOptionsDropdownXML}
                </Select>
              </FormControl>

              {/* Description - Text Input */}
              <FormControl size="small" sx={{ flex: "1", minWidth: "110px" }}>
                <TextField
                  variant="outlined"
                  size="small"
                  autoComplete="off"
                  label={t("LBL_ELEM_NAME").replace(":", "")}
                  className={"width-100"}
                  name={"DESCRIPTION"}
                  value={elementName}
                  disabled={!fieldPropTopLoaded}
                  error={elementNameErrors.length > 0}
                  helperText={elementNameErrors.length > 0 ? elementNameErrors[0] : ''}
                  onChange={(e) => {
                    return handleDescriptionChange(e.target.value, e)
                  }}
                  onBlur={(e) => {
                    return handleDescriptionBlur(e.target.value, e)
                  }}
                  onFocus={handleDescriptionFocus}
                />
              </FormControl>
            </div>
            {/* loop record conditional renders */}
            {(selectedNode?.children?.length > 0 || selectedNode?.xmlroot || selectedNode?.key === "ob-0") ? (
              <div style={{padding: "0 15px"}}>
                <FormControl variant="outlined" className="width-100" size="small">
                  <InputLabel id="loopRecordDropdownLabel">
                    {t("LBL_LOOP_REC_OPTIONAL")}
                  </InputLabel>
                  <Select
                    className="width-100"
                    MenuProps={{
                      PaperProps: {
                        sx: {
                          maxHeight: 300,
                        },
                      },
                    }}
                    name="loopRecord"
                    value={(selectedNode?.children && selectedNode?.children.length === 0 && !selectedNode?.xmlroot && selectedNode?.key !== "ob-0") ? t('SELECT') : loopRecordValue}
                    disabled={(selectedNode?.children && selectedNode?.children.length === 0) && !selectedNode?.xmlroot && selectedNode?.key !== "ob-0"}
                    label={t("LBL_LOOP_REC_OPTIONAL")}
                    onChange={handleLoopRecordChange}
                  >
                    {loopRecordMenuItems}
                  </Select>
                </FormControl>
                <RecordPropertiesCheckBox />
                <Stack spacing={"10px"} style={{marginTop: "10px"}}>
                  {loopRecordValue && !['INBOUND_DATA', 'RECORD_LOOP_CONTROL'].includes(loopRecordValue) && COMPONENT_MAPPING_OBJ[loopRecordValue]}

                  {loopRecordValue === "RECORD_LOOP_CONTROL" && (
                    <SectionHeader 
                      text={t("LBL_DATA_SOURCE")} 
                      stylesOverride={{
                        container: {
                          width: "calc(100% + 15px)",
                          marginLeft: "-15px",
                          paddingBottom: "5px",
                        }
                      }}
                    />
                  )}

                  {loopRecordValue === "INBOUND_DATA" && (
                    <div className="flex" style={{ justifyContent: "space-between" }}>
                      <Typography style={{ fontWeight: "bold" }}>
                        {t("LBL_SHOWING_LOOPS_ONLY")}
                      </Typography>
                      {/* <SectionHeader text={t("LBL_SHOWING_LOOPS_ONLY")} /> */}
                    </div>
                  )}

                  {loopRecordValue && ['INBOUND_DATA', 'RECORD_LOOP_CONTROL'].includes(loopRecordValue) && COMPONENT_MAPPING_OBJ[loopRecordValue]}
                  {/* {(showCheckbox && !showCheckboxBefore) && <RecordPropertiesCheckBox />} */}
                </Stack>
              </div>
            ) : ""}
          </div>

          {/* Data Source Section */}
          {(fieldTypeDefault !== "CONSTANT" && 
            !(templateHeader?.FILETYPE === "XML" && (fieldTypeDefault === "PACKED_INT" || fieldTypeDefault === "ZONED_INT"))) &&
            <Accordion sx={styles.accordianContainer} defaultExpanded>
              <AccordionSummary
                expandIcon={<ExpandMoreIcon />}
                aria-controls="accordianDataSource"
                className="accordian-override"
                id="accordianDataSource"
                sx={{ ...styles.accordianHeader, minHeight: "50px !important", height: "50px !important" }}
              >
                <SectionHeader text={t("LBL_DATA_SOURCE")} />
              </AccordionSummary>
              <AccordionDetails sx={{ ...styles.accordianContentFO }}>
                {/* <DataSourceSection /> */}
                <SourceType
                  // node={{
                  //   PARENT: selectedNode.PARENT && selectedNode.RECORD_TYPE ? selectedNode.PARENT+'\u001e'+selectedNode.RECORD_TYPE 
                  //     : selectedNode.PARENT ? selectedNode.PARENT 
                  //     : selectedNode.RECORD_TYPE,
                  //   RECORD_TYPE: selectedNode.RECORD_TYPE,
                  //   MAPNAME: selectedNode.MAPNAME ,
                  //   key: selectedNode.key
                  // }}
                  // node={{
                  //   PARENT: selectedNode?.PARENT,
                  //   MAPNAME: selectedNode?.MAPNAME,
                  //   RECORD_TYPE: selectedNode?.RECORD_TYPE,
                  //   key: selectedNode?.key,
                  //   ACTION: selectedNode?.ACTION,
                  //   INDEX: selectedNode?.INDEX,
                  //   MAP_REPETITION_INDEX: selectedNode?.MAP_REPETITION_INDEX,
                  //   CUSTOM_DB_FIELD: selectedNode?.CUSTOM_DB_FIELD,
                  //   PLUGIN_NAME: selectedNode?.PLUGIN_NAME
                  // }}
                  node={{
                    PARENT: selectedNode?.FIELD?.PARENT || selectedNode?.PARENT,
                    MAPNAME: selectedNode?.FIELD?.MAPNAME || selectedNode?.MAPNAME,
                    RECORD_TYPE: selectedNode?.FIELD?.RECORD_TYPE || selectedNode?.RECORD_TYPE,
                    key: selectedNode?.key,
                    ACTION: selectedNode?.FIELD?.ACTION || selectedNode?.ACTION,
                    INDEX: selectedNode?.FIELD?.INDEX || selectedNode?.INDEX,
                    MAP_REPETITION_INDEX: selectedNode?.FIELD?.MAP_REPETITION_INDEX || selectedNode?.MAP_REPETITION_INDEX,
                    CUSTOM_DB_FIELD: selectedNode?.FIELD?.CUSTOM_DB_FIELD || selectedNode?.CUSTOM_DB_FIELD,
                    PLUGIN_NAME: selectedNode?.FIELD?.PLUGIN_NAME || selectedNode?.PLUGIN_NAME,
                    WF_ITEM_ID: selectedNode?.FIELD?.WF_ITEM_ID || selectedNode?.WF_ITEM_ID,
                    WF_ITEM_LOCATION: selectedNode?.FIELD?.WF_ITEM_LOCATION || selectedNode?.WF_ITEM_LOCATION,
                    SOURCE_REPEATING_FIELD: selectedNode?.FIELD?.SOURCE_REPEATING_FIELD || selectedNode?.SOURCE_REPEATING_FIELD
                  }}
                  valuesHandler={sourceTypeValueChange}
                  initialSelectCall={true} // Initial call required so added this props
                  sourceTypeDropdownChange={sourceTypeDropdownChange}
                // callerComponent='recordProperties'
                />
              </AccordionDetails>
            </Accordion>
          }


          {/* Formatting Options Section */}
          {!(templateHeader?.FILETYPE === "XML" && (fieldTypeDefault === "PACKED_INT" || fieldTypeDefault === "ZONED_INT")) &&
            <Accordion sx={styles.accordianContainer} defaultExpanded>
              <AccordionSummary
                expandIcon={<ExpandMoreIcon />}
                aria-controls="accordianFormattingOptions"
                id="accordianFormattingOptions"
                className="accordian-override"
                sx={{ ...styles.accordianHeader, minHeight: "50px !important", height: "50px !important" }}
              >
                <SectionHeader text={t("LBL_FORMATE_OPTIONS")} />
              </AccordionSummary>
              <AccordionDetails sx={styles.accordianContentFO}>
                <ElementFormattingOptionsSection
                  updatedSelectedNode={updatedSelectedNode}
                  setUpdatedSelectedNode={setUpdatedSelectedNode}
                  sourceNodeInfo={sourceNodeInfo}
                  ftClone={ftClone}
                  hasField={updatedSelectedNode?.FIELD !== undefined}
                />
              </AccordionDetails>
            </Accordion>
          }

          {!(templateHeader?.FILETYPE === "XML" && (fieldTypeDefault === "PACKED_INT" || fieldTypeDefault === "ZONED_INT")) &&
            <div style={{ padding: "10px 15px 0 15px" }}>
              {/* Advanced Properties */}
              <AdvancedProperties 
                selectedNode={{
                  ...selectedNode,
                  // Provide advanced properties from FIELD if they exist, otherwise from selectedNode directly
                  SUBSTRING: updatedSelectedNode?.FIELD?.SUBSTRING || selectedNode?.FIELD?.SUBSTRING || selectedNode?.SUBSTRING,
                  FUNCTION: updatedSelectedNode?.FIELD?.FUNCTION || selectedNode?.FIELD?.FUNCTION || selectedNode?.FUNCTION,
                  MASK: updatedSelectedNode?.FIELD?.MASK || selectedNode?.FIELD?.MASK || selectedNode?.MASK,
                  REPLACE: updatedSelectedNode?.FIELD?.REPLACE || selectedNode?.FIELD?.REPLACE || selectedNode?.REPLACE,
                  FIELDTYPE: selectedNode?.FIELD?.FIELDTYPE || fieldType
                }} 
                updatedSelectedNodeWithAdvanceProperties={updatedSelectedNodeWithAdvanceProperties} 
              />
            </div>
          }

          {!(templateHeader?.FILETYPE === "XML" && (fieldTypeDefault === "PACKED_INT" || fieldTypeDefault === "ZONED_INT")) &&
            <Accordion sx={styles.accordianContainer} defaultExpanded>
              <AccordionSummary
                expandIcon={<ExpandMoreIcon />}
                aria-controls="accordianProcessingOptions"
                id="accordianProcessingOptions"
                className="accordian-override"
                sx={{ ...styles.accordianHeader, minHeight: "50px !important", height: "50px !important" }}
              >
                <SectionHeader text={t("LBL_PROCESSING_OPTIONS").replace(":", "")} />
              </AccordionSummary>
              <AccordionDetails sx={styles.accordianContentFO}>
                <ProcessingOptionsSection updatedSelectedNode={updatedSelectedNode} setUpdatedSelectedNode={setUpdatedSelectedNode} />
              </AccordionDetails>
            </Accordion>
          }

        </div>
      ) : (
        <div className="height-100">
          <div className="panel-no-data">{t('NO_FLD_SELECTED')}</div>
        </div>
      )}
    </>
  );
}


const fieldTypeOptionsDropdownXML = [
  <MenuItem key={1} value={"SELECT"}>{t("SELECT")}</MenuItem>,
  <MenuItem key={2} value={"ALPHA"}>{t("ALPHA")}</MenuItem>,
  <MenuItem key={3} value={"BIT_FLAG"}>{t("BIT_FLAG")}</MenuItem>,
  <MenuItem key={4} value={"CONSTANT"}>{t("CONSTANT")}</MenuItem>,
  <MenuItem key={5} value={"DATE"}>{t("DATE")}</MenuItem>,
  <MenuItem key={6} value={"FLOAT"}>{t("FLOAT")}</MenuItem>,
  <MenuItem key={7} value={"INTEGER"}>{t("INTEGER")}</MenuItem>,
  <MenuItem key={8} value={"PACKED_INT"}>{t("PACKED_INT")}</MenuItem>,
  <MenuItem key={9} value={"ZONED_INT"}>{t("ZONED_INT")}</MenuItem>,
];

const styles = {
  fpContainer: {
    // padding: "0 16px 16px 16px"
    padding: "0 4px 4px 4px"
  },
  accordianContainer: {
    border: "none",
    padding: "0",
    margin: "0",
    boxShadow: "none",
    backgroundColor: "unset"
  },
  accordianHeader: {
    border: "none",
    padding: "0",
    margin: "0",
    backgroundColor: "unset"
  },
  accordianContent: {
    border: "none",
    padding: "0 15px 0 15px",
    margin: "0",
    backgroundColor: "unset"
  },
  accordianContentFO: {
    border: "none",
    padding: "0 15px",
    margin: "0",
    backgroundColor: "unset"
  },
}

//control options for each file + field type. used for remove/add when field type changes.
const fieldpropertiesControls = {
  "ALL": [
    "CONVERT_CASE",
    "PADDIR",
    "FILLCHAR",
    "LENGTH",
    "TRIMDATA",
    "QUOTE_DATA",
    "URLENCODE",
    "SUPPRESS_DELIMITER",
    "IGNORE_GLOBAL_SUB",
    "RPT_TRUNC_ERR",
    "KEEP_ORIG_DATA_ON_ERROR",
    "DEFAULT_VALUE",
    "VALUE",
    "REPEAT",
    "INBOUND_PATTERN",
    "PATTERN",
    "TIMEZONE",
    "PRECISION",
    "ROUND",
    "PRECISION_OUT",
    "DEC_PATTERN",
    "UNSIGNED",
    "REQUIRED",
    "CONVERT_EMPTY",
    "CONVERT_NULL",
    "INCLUDE_NULL",
    "FIELD_ID",
    "REPEATING",
    "ESCAPE_DATA",
    "SUPP_ATTRIB_MSSNG_FIELD_IDENTIFIER",
    "ESCAPE_XML_DATA",
  ],
  "CSV": {
    "ALPHA": [
      //field props
      "CONVERT_CASE", "PADDIR", "FILLCHAR", "LENGTH",
      //checkboxes
      "TRIMDATA", "QUOTE_DATA", "URLENCODE", "SUPPRESS_DELIMITER", "IGNORE_GLOBAL_SUB", "RPT_TRUNC_ERR"
    ],
    "BIT_FLAG": [
      "LENGTH", "KEEP_ORIG_DATA_ON_ERROR", "DEFAULT_VALUE",
      "TRIMDATA", "QUOTE_DATA", "URLENCODE", "SUPPRESS_DELIMITER", "IGNORE_GLOBAL_SUB", "RPT_TRUNC_ERR"
    ],
    "CONSTANT": [
      "VALUE", "REPEAT", "LENGTH",
      "QUOTE_DATA", "URLENCODE", "SUPPRESS_DELIMITER", "IGNORE_GLOBAL_SUB", "RPT_TRUNC_ERR"
    ],
    "DATE": [
      "INBOUND_PATTERN", "PATTERN", "TIMEZONE", "PADDIR", "FILLCHAR", "LENGTH", "KEEP_ORIG_DATA_ON_ERROR", "DEFAULT_VALUE",
      "TRIMDATA", "QUOTE_DATA", "URLENCODE", "SUPPRESS_DELIMITER", "IGNORE_GLOBAL_SUB", "RPT_TRUNC_ERR"
    ],
    "FLOAT": [
      "PATTERN", "PADDIR", "FILLCHAR", "LENGTH", "PRECISION", "KEEP_ORIG_DATA_ON_ERROR", "DEFAULT_VALUE",
      "TRIMDATA", "QUOTE_DATA", "URLENCODE", "SUPPRESS_DELIMITER", "IGNORE_GLOBAL_SUB", "ROUND", "RPT_TRUNC_ERR"
    ],
    "INTEGER": [
      "PATTERN", "PADDIR", "FILLCHAR", "LENGTH", "PRECISION", "PRECISION_OUT", "KEEP_ORIG_DATA_ON_ERROR", "DEFAULT_VALUE",
      "TRIMDATA", "QUOTE_DATA", "URLENCODE", "SUPPRESS_DELIMITER", "IGNORE_GLOBAL_SUB", "RPT_TRUNC_ERR"
    ],
    "PACKED_INT": [
      "PATTERN", "DEC_PATTERN", "UNSIGNED", "LENGTH", "PRECISION", "KEEP_ORIG_DATA_ON_ERROR", "DEFAULT_VALUE",
      "TRIMDATA", "QUOTE_DATA", "URLENCODE", "SUPPRESS_DELIMITER", "IGNORE_GLOBAL_SUB", "RPT_TRUNC_ERR"
    ],
    "ZONED_INT": [
      "PATTERN", "DEC_PATTERN", "UNSIGNED", "LENGTH", "PRECISION", "KEEP_ORIG_DATA_ON_ERROR", "DEFAULT_VALUE",
      "TRIMDATA", "QUOTE_DATA", "URLENCODE", "SUPPRESS_DELIMITER", "IGNORE_GLOBAL_SUB", "RPT_TRUNC_ERR"
    ],
  },
  "DATABASE": {
    "ALPHA": [
      "CONVERT_CASE", "PADDIR", "FILLCHAR", "LENGTH",
      "TRIMDATA", "QUOTE_DATA", "URLENCODE", "IGNORE_GLOBAL_SUB", "REQUIRED", "CONVERT_EMPTY", "CONVERT_NULL", "INCLUDE_NULL", "RPT_TRUNC_ERR"
    ],
    "BIT_FLAG": [
      "LENGTH", "KEEP_ORIG_DATA_ON_ERROR", "DEFAULT_VALUE",
      "TRIMDATA", "QUOTE_DATA", "URLENCODE", "IGNORE_GLOBAL_SUB", "REQUIRED", "CONVERT_EMPTY", "CONVERT_NULL", "INCLUDE_NULL", "RPT_TRUNC_ERR"
    ],
    "CONSTANT": [
      "VALUE", "REPEAT", "LENGTH",
      "QUOTE_DATA", "URLENCODE", "IGNORE_GLOBAL_SUB", "REQUIRED", "CONVERT_EMPTY", "CONVERT_NULL", "INCLUDE_NULL", "RPT_TRUNC_ERR"
    ],
    "DATE": [
      "INBOUND_PATTERN", "PATTERN", "PADDIR", "FILLCHAR", "LENGTH", "KEEP_ORIG_DATA_ON_ERROR", "DEFAULT_VALUE",
      "TRIMDATA", "QUOTE_DATA", "URLENCODE", "IGNORE_GLOBAL_SUB", "REQUIRED", "CONVERT_EMPTY", "CONVERT_NULL", "INCLUDE_NULL", "RPT_TRUNC_ERR"
    ],
    "FLOAT": [
      "PATTERN", "PADDIR", "FILLCHAR", "LENGTH", "PRECISION", "KEEP_ORIG_DATA_ON_ERROR", "DEFAULT_VALUE",
      "TRIMDATA", "QUOTE_DATA", "URLENCODE", "IGNORE_GLOBAL_SUB", "REQUIRED", "CONVERT_EMPTY", "CONVERT_NULL", "INCLUDE_NULL", "ROUND", "RPT_TRUNC_ERR"
    ],
    "INTEGER": [
      "PATTERN", "PADDIR", "FILLCHAR", "LENGTH", "PRECISION", "PRECISION_OUT", "KEEP_ORIG_DATA_ON_ERROR", "DEFAULT_VALUE",
      "TRIMDATA", "QUOTE_DATA", "URLENCODE", "IGNORE_GLOBAL_SUB", "REQUIRED", "CONVERT_EMPTY", "CONVERT_NULL", "INCLUDE_NULL", "RPT_TRUNC_ERR"
    ],
    "PACKED_INT": [
      "PATTERN", "DEC_PATTERN", "UNSIGNED", "LENGTH", "PRECISION", "KEEP_ORIG_DATA_ON_ERROR", "DEFAULT_VALUE",
      "TRIMDATA", "QUOTE_DATA", "URLENCODE", "IGNORE_GLOBAL_SUB", "REQUIRED", "CONVERT_EMPTY", "CONVERT_NULL", "INCLUDE_NULL", "RPT_TRUNC_ERR"
    ],
    "ZONED_INT": [
      "PATTERN", "DEC_PATTERN", "UNSIGNED", "LENGTH", "PRECISION", "KEEP_ORIG_DATA_ON_ERROR", "DEFAULT_VALUE",
      "TRIMDATA", "QUOTE_DATA", "URLENCODE", "IGNORE_GLOBAL_SUB", "REQUIRED", "CONVERT_EMPTY", "CONVERT_NULL", "INCLUDE_NULL", "RPT_TRUNC_ERR"
    ],
  },
  "DELIMITED": {
    "ALPHA": [
      "CONVERT_CASE", "PADDIR", "FILLCHAR", "LENGTH",
      "TRIMDATA", "QUOTE_DATA", "URLENCODE", "SUPPRESS_DELIMITER", "IGNORE_GLOBAL_SUB", "RPT_TRUNC_ERR"
    ],
    "BIT_FLAG": [
      "LENGTH", "KEEP_ORIG_DATA_ON_ERROR", "DEFAULT_VALUE",
      "TRIMDATA", "QUOTE_DATA", "URLENCODE", "SUPPRESS_DELIMITER", "IGNORE_GLOBAL_SUB", "RPT_TRUNC_ERR"
    ],
    "CONSTANT": [
      "VALUE", "REPEAT", "LENGTH",
      "QUOTE_DATA", "URLENCODE", "SUPPRESS_DELIMITER", "IGNORE_GLOBAL_SUB", "RPT_TRUNC_ERR"
    ],
    "DATE": [
      "INBOUND_PATTERN", "PATTERN", "TIMEZONE", "PADDIR", "FILLCHAR", "LENGTH", "KEEP_ORIG_DATA_ON_ERROR", "DEFAULT_VALUE",
      "TRIMDATA", "QUOTE_DATA", "URLENCODE", "SUPPRESS_DELIMITER", "IGNORE_GLOBAL_SUB", "RPT_TRUNC_ERR"
    ],
    "FLOAT": [
      "PATTERN", "PADDIR", "FILLCHAR", "LENGTH", "PRECISION", "KEEP_ORIG_DATA_ON_ERROR", "DEFAULT_VALUE",
      "TRIMDATA", "QUOTE_DATA", "URLENCODE", "SUPPRESS_DELIMITER", "IGNORE_GLOBAL_SUB", "ROUND", "RPT_TRUNC_ERR"
    ],
    "INTEGER": [
      "PATTERN", "PADDIR", "FILLCHAR", "LENGTH", "PRECISION", "PRECISION_OUT", "KEEP_ORIG_DATA_ON_ERROR", "DEFAULT_VALUE",
      "TRIMDATA", "QUOTE_DATA", "URLENCODE", "SUPPRESS_DELIMITER", "IGNORE_GLOBAL_SUB", "RPT_TRUNC_ERR"
    ],
    "PACKED_INT": [
      "PATTERN", "DEC_PATTERN", "UNSIGNED", "LENGTH", "PRECISION", "KEEP_ORIG_DATA_ON_ERROR", "DEFAULT_VALUE",
      "TRIMDATA", "QUOTE_DATA", "URLENCODE", "SUPPRESS_DELIMITER", "IGNORE_GLOBAL_SUB", "RPT_TRUNC_ERR"
    ],
    "ZONED_INT": [
      "PATTERN", "DEC_PATTERN", "UNSIGNED", "LENGTH", "PRECISION", "KEEP_ORIG_DATA_ON_ERROR", "DEFAULT_VALUE",
      "TRIMDATA", "QUOTE_DATA", "URLENCODE", "SUPPRESS_DELIMITER", "IGNORE_GLOBAL_SUB", "RPT_TRUNC_ERR"
    ],
  },
  "DELIM_FLD_ID": {
    "ALPHA": [
      "CONVERT_CASE", "PADDIR", "FILLCHAR", "LENGTH", "FIELD_ID",
      "TRIMDATA", "QUOTE_DATA", "URLENCODE", "SUPPRESS_DELIMITER", "IGNORE_GLOBAL_SUB", "RPT_TRUNC_ERR", "REPEATING",
    ],
    "BIT_FLAG": [
      "LENGTH", "KEEP_ORIG_DATA_ON_ERROR", "DEFAULT_VALUE", "FIELD_ID",
      "TRIMDATA", "QUOTE_DATA", "URLENCODE", "SUPPRESS_DELIMITER", "IGNORE_GLOBAL_SUB", "RPT_TRUNC_ERR", "REPEATING",
    ],
    "CONSTANT": [
      "VALUE", "REPEAT", "LENGTH", "FIELD_ID",
      "QUOTE_DATA", "URLENCODE", "SUPPRESS_DELIMITER", "IGNORE_GLOBAL_SUB", "RPT_TRUNC_ERR", "REPEATING",
    ],
    "DATE": [
      "INBOUND_PATTERN", "PATTERN", "TIMEZONE", "PADDIR", "FILLCHAR", "LENGTH", "KEEP_ORIG_DATA_ON_ERROR", "DEFAULT_VALUE", "FIELD_ID",
      "TRIMDATA", "QUOTE_DATA", "URLENCODE", "SUPPRESS_DELIMITER", "IGNORE_GLOBAL_SUB", "RPT_TRUNC_ERR", "REPEATING",
    ],
    "FLOAT": [
      "PATTERN", "PADDIR", "FILLCHAR", "LENGTH", "PRECISION", "KEEP_ORIG_DATA_ON_ERROR", "DEFAULT_VALUE", "FIELD_ID",
      "TRIMDATA", "QUOTE_DATA", "URLENCODE", "SUPPRESS_DELIMITER", "IGNORE_GLOBAL_SUB", "ROUND", "RPT_TRUNC_ERR", "REPEATING",
    ],
    "INTEGER": [
      "PATTERN", "PADDIR", "FILLCHAR", "LENGTH", "PRECISION", "PRECISION_OUT", "KEEP_ORIG_DATA_ON_ERROR", "DEFAULT_VALUE", "FIELD_ID",
      "TRIMDATA", "QUOTE_DATA", "URLENCODE", "SUPPRESS_DELIMITER", "IGNORE_GLOBAL_SUB", "RPT_TRUNC_ERR", "REPEATING",
    ],
    "PACKED_INT": [
      "PATTERN", "DEC_PATTERN", "UNSIGNED", "LENGTH", "PRECISION", "KEEP_ORIG_DATA_ON_ERROR", "DEFAULT_VALUE", "FIELD_ID",
      "TRIMDATA", "QUOTE_DATA", "URLENCODE", "SUPPRESS_DELIMITER", "IGNORE_GLOBAL_SUB", "RPT_TRUNC_ERR", "REPEATING",
    ],
    "ZONED_INT": [
      "PATTERN", "DEC_PATTERN", "UNSIGNED", "LENGTH", "PRECISION", "KEEP_ORIG_DATA_ON_ERROR", "DEFAULT_VALUE", "FIELD_ID",
      "TRIMDATA", "QUOTE_DATA", "URLENCODE", "SUPPRESS_DELIMITER", "IGNORE_GLOBAL_SUB", "RPT_TRUNC_ERR", "REPEATING",
    ],
  },
  "EDI": {
    "ALPHA": [
      "CONVERT_CASE", "PADDIR", "FILLCHAR", "LENGTH",
      "TRIMDATA", "QUOTE_DATA", "URLENCODE", "SUPPRESS_DELIMITER", "IGNORE_GLOBAL_SUB", "RPT_TRUNC_ERR",
    ],
    "BIT_FLAG": [
      "LENGTH", "KEEP_ORIG_DATA_ON_ERROR", "DEFAULT_VALUE",
      "TRIMDATA", "QUOTE_DATA", "URLENCODE", "SUPPRESS_DELIMITER", "IGNORE_GLOBAL_SUB", "RPT_TRUNC_ERR",
    ],
    "CONSTANT": [
      "VALUE", "REPEAT", "LENGTH",
      "QUOTE_DATA", "URLENCODE", "SUPPRESS_DELIMITER", "IGNORE_GLOBAL_SUB", "RPT_TRUNC_ERR",
    ],
    "DATE": [
      "INBOUND_PATTERN", "PATTERN", "TIMEZONE", "PADDIR", "FILLCHAR", "LENGTH", "KEEP_ORIG_DATA_ON_ERROR", "DEFAULT_VALUE",
      "TRIMDATA", "QUOTE_DATA", "URLENCODE", "SUPPRESS_DELIMITER", "IGNORE_GLOBAL_SUB", "RPT_TRUNC_ERR",
    ],
    "FLOAT": [
      "PATTERN", "PADDIR", "FILLCHAR", "LENGTH", "PRECISION", "KEEP_ORIG_DATA_ON_ERROR", "DEFAULT_VALUE",
      "TRIMDATA", "QUOTE_DATA", "URLENCODE", "SUPPRESS_DELIMITER", "IGNORE_GLOBAL_SUB", "ROUND", "RPT_TRUNC_ERR",
    ],
    "INTEGER": [
      "PATTERN", "PADDIR", "FILLCHAR", "LENGTH", "PRECISION", "PRECISION_OUT", "KEEP_ORIG_DATA_ON_ERROR", "DEFAULT_VALUE",
      "TRIMDATA", "QUOTE_DATA", "URLENCODE", "SUPPRESS_DELIMITER", "IGNORE_GLOBAL_SUB", "RPT_TRUNC_ERR",
    ],
    "PACKED_INT": [
      "PATTERN", "DEC_PATTERN", "UNSIGNED", "LENGTH", "PRECISION", "KEEP_ORIG_DATA_ON_ERROR", "DEFAULT_VALUE",
      "TRIMDATA", "QUOTE_DATA", "URLENCODE", "SUPPRESS_DELIMITER", "IGNORE_GLOBAL_SUB", "RPT_TRUNC_ERR",
    ],
    "ZONED_INT": [
      "PATTERN", "DEC_PATTERN", "UNSIGNED", "LENGTH", "PRECISION", "KEEP_ORIG_DATA_ON_ERROR", "DEFAULT_VALUE",
      "TRIMDATA", "QUOTE_DATA", "URLENCODE", "SUPPRESS_DELIMITER", "IGNORE_GLOBAL_SUB", "RPT_TRUNC_ERR",
    ],
  },
  "FIXED_LENGTH": {
    "ALPHA": [
      "CONVERT_CASE", "PADDIR", "FILLCHAR", "LENGTH",
      "QUOTE_DATA", "URLENCODE", "SUPPRESS_DELIMITER", "IGNORE_GLOBAL_SUB", "RPT_TRUNC_ERR",
    ],
    "BIT_FLAG": [
      "LENGTH", "KEEP_ORIG_DATA_ON_ERROR", "DEFAULT_VALUE",
      "QUOTE_DATA", "URLENCODE", "SUPPRESS_DELIMITER", "IGNORE_GLOBAL_SUB", "RPT_TRUNC_ERR",
    ],
    "CONSTANT": [
      "VALUE", "REPEAT", "LENGTH",
      "QUOTE_DATA", "URLENCODE", "SUPPRESS_DELIMITER", "IGNORE_GLOBAL_SUB", "RPT_TRUNC_ERR",
    ],
    "DATE": [
      "INBOUND_PATTERN", "PATTERN", "TIMEZONE", "PADDIR", "FILLCHAR", "LENGTH", "KEEP_ORIG_DATA_ON_ERROR", "DEFAULT_VALUE",
      "QUOTE_DATA", "URLENCODE", "SUPPRESS_DELIMITER", "IGNORE_GLOBAL_SUB", "RPT_TRUNC_ERR",
    ],
    "FLOAT": [
      "PATTERN", "PADDIR", "FILLCHAR", "LENGTH", "PRECISION", "KEEP_ORIG_DATA_ON_ERROR", "DEFAULT_VALUE",
      "QUOTE_DATA", "URLENCODE", "SUPPRESS_DELIMITER", "IGNORE_GLOBAL_SUB", "ROUND", "RPT_TRUNC_ERR",
    ],
    "INTEGER": [
      "PATTERN", "PADDIR", "FILLCHAR", "LENGTH", "PRECISION", "PRECISION_OUT", "KEEP_ORIG_DATA_ON_ERROR", "DEFAULT_VALUE",
      "QUOTE_DATA", "URLENCODE", "SUPPRESS_DELIMITER", "IGNORE_GLOBAL_SUB", "RPT_TRUNC_ERR",
    ],
    "PACKED_INT": [
      "PATTERN", "DEC_PATTERN", "UNSIGNED", "LENGTH", "PRECISION", "KEEP_ORIG_DATA_ON_ERROR", "DEFAULT_VALUE",
      "QUOTE_DATA", "URLENCODE", "SUPPRESS_DELIMITER", "IGNORE_GLOBAL_SUB", "RPT_TRUNC_ERR",
    ],
    "ZONED_INT": [
      "PATTERN", "DEC_PATTERN", "UNSIGNED", "LENGTH", "PRECISION", "KEEP_ORIG_DATA_ON_ERROR", "DEFAULT_VALUE",
      "QUOTE_DATA", "URLENCODE", "SUPPRESS_DELIMITER", "IGNORE_GLOBAL_SUB", "RPT_TRUNC_ERR",
    ],
  },
  "HL7": {
    "ALPHA": [
      "CONVERT_CASE", "PADDIR", "FILLCHAR", "LENGTH",
      "TRIMDATA", "QUOTE_DATA", "URLENCODE", "SUPPRESS_DELIMITER", "IGNORE_GLOBAL_SUB", "RPT_TRUNC_ERR",
    ],
    "BIT_FLAG": [
      "LENGTH", "KEEP_ORIG_DATA_ON_ERROR", "DEFAULT_VALUE",
      "TRIMDATA", "QUOTE_DATA", "URLENCODE", "SUPPRESS_DELIMITER", "IGNORE_GLOBAL_SUB", "RPT_TRUNC_ERR",
    ],
    "CONSTANT": [
      "VALUE", "REPEAT", "LENGTH",
      "QUOTE_DATA", "URLENCODE", "SUPPRESS_DELIMITER", "IGNORE_GLOBAL_SUB", "RPT_TRUNC_ERR",
    ],
    "DATE": [
      "INBOUND_PATTERN", "PATTERN", "TIMEZONE", "PADDIR", "FILLCHAR", "LENGTH", "KEEP_ORIG_DATA_ON_ERROR", "DEFAULT_VALUE",
      "TRIMDATA", "QUOTE_DATA", "URLENCODE", "SUPPRESS_DELIMITER", "IGNORE_GLOBAL_SUB", "RPT_TRUNC_ERR",
    ],
    "FLOAT": [
      "PATTERN", "PADDIR", "FILLCHAR", "LENGTH", "PRECISION", "KEEP_ORIG_DATA_ON_ERROR", "DEFAULT_VALUE",
      "TRIMDATA", "QUOTE_DATA", "URLENCODE", "SUPPRESS_DELIMITER", "IGNORE_GLOBAL_SUB", "ROUND", "RPT_TRUNC_ERR",
    ],
    "INTEGER": [
      "PATTERN", "PADDIR", "FILLCHAR", "LENGTH", "PRECISION", "PRECISION_OUT", "KEEP_ORIG_DATA_ON_ERROR", "DEFAULT_VALUE",
      "TRIMDATA", "QUOTE_DATA", "URLENCODE", "SUPPRESS_DELIMITER", "IGNORE_GLOBAL_SUB", "RPT_TRUNC_ERR",
    ],
    "PACKED_INT": [
      "PATTERN", "DEC_PATTERN", "UNSIGNED", "LENGTH", "PRECISION", "KEEP_ORIG_DATA_ON_ERROR", "DEFAULT_VALUE",
      "TRIMDATA", "QUOTE_DATA", "URLENCODE", "SUPPRESS_DELIMITER", "IGNORE_GLOBAL_SUB", "RPT_TRUNC_ERR",
    ],
    "ZONED_INT": [
      "PATTERN", "DEC_PATTERN", "UNSIGNED", "LENGTH", "PRECISION", "KEEP_ORIG_DATA_ON_ERROR", "DEFAULT_VALUE",
      "TRIMDATA", "QUOTE_DATA", "URLENCODE", "SUPPRESS_DELIMITER", "IGNORE_GLOBAL_SUB", "RPT_TRUNC_ERR",
    ],
  },
  "JSON": {
    "ALPHA": [
      "CONVERT_CASE", "PADDIR", "FILLCHAR", "LENGTH",
      "TRIMDATA", "QUOTE_DATA", "URLENCODE", "IGNORE_GLOBAL_SUB", "RPT_TRUNC_ERR", "CONVERT_EMPTY", "CONVERT_NULL", "INCLUDE_NULL", "ESCAPE_DATA", "REPEATING"
    ],
    "BIT_FLAG": [
      "LENGTH", "KEEP_ORIG_DATA_ON_ERROR", "DEFAULT_VALUE",
      "TRIMDATA", "QUOTE_DATA", "URLENCODE", "IGNORE_GLOBAL_SUB", "RPT_TRUNC_ERR", "CONVERT_EMPTY", "CONVERT_NULL", "INCLUDE_NULL", "ESCAPE_DATA", "REPEATING"
    ],
    "CONSTANT": [
      "VALUE", "REPEAT", "LENGTH",
      "QUOTE_DATA", "URLENCODE", "IGNORE_GLOBAL_SUB", "RPT_TRUNC_ERR", "CONVERT_EMPTY", "CONVERT_NULL", "INCLUDE_NULL", "ESCAPE_DATA", "REPEATING"
    ],
    "DATE": [
      "INBOUND_PATTERN", "PATTERN", "TIMEZONE", "PADDIR", "FILLCHAR", "LENGTH", "KEEP_ORIG_DATA_ON_ERROR", "DEFAULT_VALUE",
      "TRIMDATA", "QUOTE_DATA", "URLENCODE", "IGNORE_GLOBAL_SUB", "RPT_TRUNC_ERR", "CONVERT_EMPTY", "CONVERT_NULL", "INCLUDE_NULL", "ESCAPE_DATA", "REPEATING"
    ],
    "FLOAT": [
      "PATTERN", "PADDIR", "FILLCHAR", "LENGTH", "PRECISION", "KEEP_ORIG_DATA_ON_ERROR", "DEFAULT_VALUE",
      "TRIMDATA", "QUOTE_DATA", "URLENCODE", "IGNORE_GLOBAL_SUB", "RPT_TRUNC_ERR", "ROUND", "CONVERT_EMPTY", "CONVERT_NULL", "INCLUDE_NULL", "ESCAPE_DATA", "REPEATING"
    ],
    "INTEGER": [
      "PATTERN", "PADDIR", "FILLCHAR", "LENGTH", "PRECISION", "PRECISION_OUT", "KEEP_ORIG_DATA_ON_ERROR", "DEFAULT_VALUE",
      "TRIMDATA", "QUOTE_DATA", "URLENCODE", "IGNORE_GLOBAL_SUB", "RPT_TRUNC_ERR", "CONVERT_EMPTY", "CONVERT_NULL", "INCLUDE_NULL", "ESCAPE_DATA", "REPEATING"
    ],
    "PACKED_INT": [
      "PATTERN", "DEC_PATTERN", "UNSIGNED", "LENGTH", "PRECISION", "KEEP_ORIG_DATA_ON_ERROR", "DEFAULT_VALUE",
      "TRIMDATA", "QUOTE_DATA", "URLENCODE", "IGNORE_GLOBAL_SUB", "RPT_TRUNC_ERR", "CONVERT_EMPTY", "CONVERT_NULL", "INCLUDE_NULL", "ESCAPE_DATA", "REPEATING"
    ],
    "ZONED_INT": [
      "PATTERN", "DEC_PATTERN", "UNSIGNED", "LENGTH", "PRECISION", "KEEP_ORIG_DATA_ON_ERROR", "DEFAULT_VALUE",
      "TRIMDATA", "QUOTE_DATA", "URLENCODE", "IGNORE_GLOBAL_SUB", "RPT_TRUNC_ERR", "CONVERT_EMPTY", "CONVERT_NULL", "INCLUDE_NULL", "ESCAPE_DATA", "REPEATING"
    ],
  },
  "MULIT_DELIMITED": {
    "ALPHA": [
      "CONVERT_CASE", "PADDIR", "FILLCHAR", "LENGTH",
      "TRIMDATA", "QUOTE_DATA", "URLENCODE", "SUPPRESS_DELIMITER", "IGNORE_GLOBAL_SUB", "RPT_TRUNC_ERR", "REPEATING",
    ],
    "BIT_FLAG": [
      "LENGTH", "KEEP_ORIG_DATA_ON_ERROR", "DEFAULT_VALUE",
      "TRIMDATA", "QUOTE_DATA", "URLENCODE", "SUPPRESS_DELIMITER", "IGNORE_GLOBAL_SUB", "RPT_TRUNC_ERR", "REPEATING",
    ],
    "CONSTANT": [
      "VALUE", "REPEAT", "LENGTH",
      "QUOTE_DATA", "URLENCODE", "SUPPRESS_DELIMITER", "IGNORE_GLOBAL_SUB", "RPT_TRUNC_ERR", "REPEATING",
    ],
    "DATE": [
      "INBOUND_PATTERN", "PATTERN", "TIMEZONE", "PADDIR", "FILLCHAR", "LENGTH", "KEEP_ORIG_DATA_ON_ERROR", "DEFAULT_VALUE",
      "TRIMDATA", "QUOTE_DATA", "URLENCODE", "SUPPRESS_DELIMITER", "IGNORE_GLOBAL_SUB", "RPT_TRUNC_ERR", "REPEATING",
    ],
    "FLOAT": [
      "PATTERN", "PADDIR", "FILLCHAR", "LENGTH", "PRECISION", "KEEP_ORIG_DATA_ON_ERROR", "DEFAULT_VALUE",
      "TRIMDATA", "QUOTE_DATA", "URLENCODE", "SUPPRESS_DELIMITER", "IGNORE_GLOBAL_SUB", "ROUND", "RPT_TRUNC_ERR", "REPEATING",
    ],
    "INTEGER": [
      "PATTERN", "PADDIR", "FILLCHAR", "LENGTH", "PRECISION", "PRECISION_OUT", "KEEP_ORIG_DATA_ON_ERROR", "DEFAULT_VALUE",
      "TRIMDATA", "QUOTE_DATA", "URLENCODE", "SUPPRESS_DELIMITER", "IGNORE_GLOBAL_SUB", "RPT_TRUNC_ERR", "REPEATING",
    ],
    "PACKED_INT": [
      "PATTERN", "DEC_PATTERN", "UNSIGNED", "LENGTH", "PRECISION", "KEEP_ORIG_DATA_ON_ERROR", "DEFAULT_VALUE",
      "TRIMDATA", "QUOTE_DATA", "URLENCODE", "SUPPRESS_DELIMITER", "IGNORE_GLOBAL_SUB", "RPT_TRUNC_ERR", "REPEATING",
    ],
    "ZONED_INT": [
      "PATTERN", "DEC_PATTERN", "UNSIGNED", "LENGTH", "PRECISION", "KEEP_ORIG_DATA_ON_ERROR", "DEFAULT_VALUE",
      "TRIMDATA", "QUOTE_DATA", "URLENCODE", "SUPPRESS_DELIMITER", "IGNORE_GLOBAL_SUB", "RPT_TRUNC_ERR", "REPEATING",
    ],
  },
  "SERVLET": {
    "ALPHA": [
      "CONVERT_CASE", "PADDIR", "FILLCHAR", "LENGTH",
      "TRIMDATA", "QUOTE_DATA", "URLENCODE", "SUPPRESS_DELIMITER", "IGNORE_GLOBAL_SUB", "RPT_TRUNC_ERR"
    ],
    "BIT_FLAG": [
      "LENGTH", "KEEP_ORIG_DATA_ON_ERROR", "DEFAULT_VALUE",
      "TRIMDATA", "QUOTE_DATA", "URLENCODE", "SUPPRESS_DELIMITER", "IGNORE_GLOBAL_SUB", "RPT_TRUNC_ERR"
    ],
    "CONSTANT": [
      "VALUE", "REPEAT", "LENGTH",
      "QUOTE_DATA", "URLENCODE", "SUPPRESS_DELIMITER", "IGNORE_GLOBAL_SUB", "RPT_TRUNC_ERR"
    ],
    "DATE": [
      "INBOUND_PATTERN", "PATTERN", "TIMEZONE", "PADDIR", "FILLCHAR", "LENGTH", "KEEP_ORIG_DATA_ON_ERROR", "DEFAULT_VALUE",
      "TRIMDATA", "QUOTE_DATA", "URLENCODE", "SUPPRESS_DELIMITER", "IGNORE_GLOBAL_SUB", "RPT_TRUNC_ERR"
    ],
    "FLOAT": [
      "PATTERN", "PADDIR", "FILLCHAR", "LENGTH", "PRECISION", "KEEP_ORIG_DATA_ON_ERROR", "DEFAULT_VALUE",
      "TRIMDATA", "QUOTE_DATA", "URLENCODE", "SUPPRESS_DELIMITER", "IGNORE_GLOBAL_SUB", "ROUND", "RPT_TRUNC_ERR"
    ],
    "INTEGER": [
      "PATTERN", "PADDIR", "FILLCHAR", "LENGTH", "PRECISION", "PRECISION_OUT", "KEEP_ORIG_DATA_ON_ERROR", "DEFAULT_VALUE",
      "TRIMDATA", "QUOTE_DATA", "URLENCODE", "SUPPRESS_DELIMITER", "IGNORE_GLOBAL_SUB", "RPT_TRUNC_ERR"
    ],
    "PACKED_INT": [
      "PATTERN", "DEC_PATTERN", "UNSIGNED", "LENGTH", "PRECISION", "KEEP_ORIG_DATA_ON_ERROR", "DEFAULT_VALUE",
      "TRIMDATA", "QUOTE_DATA", "URLENCODE", "SUPPRESS_DELIMITER", "IGNORE_GLOBAL_SUB", "RPT_TRUNC_ERR"
    ],
    "ZONED_INT": [
      "PATTERN", "DEC_PATTERN", "UNSIGNED", "LENGTH", "PRECISION", "KEEP_ORIG_DATA_ON_ERROR", "DEFAULT_VALUE",
      "TRIMDATA", "QUOTE_DATA", "URLENCODE", "SUPPRESS_DELIMITER", "IGNORE_GLOBAL_SUB", "RPT_TRUNC_ERR"
    ],
  },
  "XML": {
    "ALPHA": [
      "CONVERT_CASE", "PADDIR", "FILLCHAR", "LENGTH",
      "TRIMDATA", "ESCAPE_XML_DATA", "URLENCODE", "IGNORE_GLOBAL_SUB", "SUPP_ATTRIB_MSSNG_FIELD_IDENTIFIER", "REQUIRED", "RPT_TRUNC_ERR"
    ],
    "BIT_FLAG": [
      "LENGTH", "KEEP_ORIG_DATA_ON_ERROR", "DEFAULT_VALUE",
      "TRIMDATA", "ESCAPE_XML_DATA", "URLENCODE", "IGNORE_GLOBAL_SUB", "SUPP_ATTRIB_MSSNG_FIELD_IDENTIFIER", "REQUIRED", "RPT_TRUNC_ERR"
    ],
    "CONSTANT": [
      "VALUE", "REPEAT", "LENGTH",
      "ESCAPE_XML_DATA", "URLENCODE", "IGNORE_GLOBAL_SUB", "SUPP_ATTRIB_MSSNG_FIELD_IDENTIFIER", "REQUIRED", "RPT_TRUNC_ERR"
    ],
    "DATE": [
      "INBOUND_PATTERN", "PATTERN", "PADDIR", "FILLCHAR", "LENGTH", "KEEP_ORIG_DATA_ON_ERROR", "DEFAULT_VALUE",
      "TRIMDATA", "ESCAPE_XML_DATA", "URLENCODE", "IGNORE_GLOBAL_SUB", "SUPP_ATTRIB_MSSNG_FIELD_IDENTIFIER", "REQUIRED", "RPT_TRUNC_ERR"
    ],
    "FLOAT": [
      "PATTERN", "PADDIR", "FILLCHAR", "LENGTH", "PRECISION", "KEEP_ORIG_DATA_ON_ERROR", "DEFAULT_VALUE",
      "TRIMDATA", "ESCAPE_XML_DATA", "URLENCODE", "IGNORE_GLOBAL_SUB", "SUPP_ATTRIB_MSSNG_FIELD_IDENTIFIER", "REQUIRED", "ROUND", "RPT_TRUNC_ERR"
    ],
    "INTEGER": [
      "PATTERN", "PADDIR", "FILLCHAR", "LENGTH", "PRECISION", "PRECISION_OUT", "KEEP_ORIG_DATA_ON_ERROR", "DEFAULT_VALUE",
      "TRIMDATA", "ESCAPE_XML_DATA", "URLENCODE", "SUPPRESS_DELIMITER", "IGNORE_GLOBAL_SUB", "SUPP_ATTRIB_MSSNG_FIELD_IDENTIFIER", "REQUIRED", "RPT_TRUNC_ERR"
    ],
    "PACKED_INT": [],
    "ZONED_INT": [],
  },
}


//
const fs = [
  "SELECT", "ALPHA", "BIT_FLAG",
  "CONSTANT", "DATE", "FLOAT", "INTEGER",
  "PACKED_INT", "ZONED_INT"
];